import type { NotaPublicaCard } from '@ahora-patagonia/contracts';
import { api } from '../../shared/http/ApiClient.js';
import { BaseViewModel } from '../../shared/viewmodels/BaseViewModel.js';
import { SeccionRepository } from '../models/repositories/SeccionRepository.js';
import type { SeccionData, SeccionKind } from '../models/entities/SeccionData.js';

interface State {
  loading: boolean;
  /** La última página traída (shell, título, total). */
  data: SeccionData | null;
  /** Todas las notas acumuladas: "Ver más" appendea de a 12, no reemplaza. */
  items: NotaPublicaCard[];
  cargandoMas: boolean;
  error: string | null;
  hydrated: boolean;
}

/**
 * ViewModel de `/ultima-hora` y `/mas-leidas`, con "Ver más" de 12 en 12.
 *
 * Tiene `irA` por el mismo motivo que `CategoriaViewModel`, aunque acá cada sección sea su propia
 * ruta: las dos rutas renderizan **el mismo componente**, así que React lo reusa en vez de
 * remontarlo y el VM sobrevive al salto. Sin `irA`, elegir la otra del menú dejaba la pantalla
 * intacta.
 *
 * Lo único que sigue siendo más simple que la categoría: no hay estado `noEncontrada`, porque estas
 * dos páginas existen siempre.
 */
export class SeccionViewModel extends BaseViewModel<State> {
  private readonly repo = new SeccionRepository({ get: (p) => api.get(p) });
  /** La última sección pedida. Lo que vuelva de una anterior se descarta. */
  private ultimaPedida: SeccionKind;

  constructor(
    initial: SeccionData | null,
    private kind: SeccionKind,
  ) {
    super({
      loading: false,
      data: initial,
      items: initial?.items ?? [],
      cargandoMas: false,
      error: null,
      hydrated: initial !== null,
    });
    this.ultimaPedida = kind;
  }

  /** Primera carga: no-op si la página vino del server (que es el camino normal). */
  async load(): Promise<void> {
    await this.irA(this.kind);
  }

  /**
   * Muestra la otra sección **sin remontar la pantalla**: `/ultima-hora` y `/mas-leidas` son dos
   * rutas distintas pero comparten componente, así que del menú se salta de una a otra con la vista
   * ya montada.
   */
  async irA(kind: SeccionKind): Promise<void> {
    if (kind === this.kind && this.state$.value.hydrated) return;

    this.kind = kind;
    this.ultimaPedida = kind;
    // Se vacía la lista: son las notas de la sección anterior y "Ver más" pagina sobre ellas.
    // La vista muestra el esqueleto mientras tanto.
    this.patch({ loading: true, data: null, items: [], error: null, hydrated: false });

    try {
      const data = await this.repo.porPagina(kind);
      if (this.ultimaPedida !== kind) return;
      this.patch({ data, items: data.items, loading: false, hydrated: true });
    } catch (err) {
      if (this.ultimaPedida !== kind) return;
      this.patch({ loading: false, error: (err as Error).message });
      this.emit('error', err);
    }
  }

  get hayMas(): boolean {
    const { data, items } = this.state$.value;
    return data !== null && items.length < data.total;
  }

  async verMas(): Promise<void> {
    const { data, items, cargandoMas } = this.state$.value;
    if (!data || cargandoMas || !this.hayMas) return;

    this.patch({ cargandoMas: true });
    try {
      const siguiente = Math.floor(items.length / data.pageSize) + 1;
      const pagina = await this.repo.porPagina(this.kind, siguiente);
      // Se descartan las repetidas: en Más leídas el orden puede haberse movido entre dos páginas
      // (el ranking es vivo), y sin esto la misma nota podría aparecer dos veces en la lista.
      const vistos = new Set(items.map((n) => n.id));
      this.patch({
        data: pagina,
        items: [...items, ...pagina.items.filter((n) => !vistos.has(n.id))],
        cargandoMas: false,
      });
    } catch (err) {
      this.patch({ cargandoMas: false, error: (err as Error).message });
      this.emit('error', err);
    }
  }
}
